import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

interface RoundInvitation {
  id: string;
  round_id: string;
  round_code: string;
  round_name: string;
  inviter_user_id: string;
  status: string;
  created_at: string;
}

export const useRoundInvitations = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    // Check for pending invitations on mount
    const checkPendingInvitations = async () => {
      const { data: invitations, error } = await supabase
        .from("round_invitations")
        .select("*")
        .eq("invited_user_id", user.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) {
        console.error("Error checking invitations:", error);
        return;
      }

      if (invitations && invitations.length > 0) {
        handleInvitation(invitations[0] as RoundInvitation);
      }
    };

    checkPendingInvitations();

    // Subscribe to real-time invitation inserts
    const channel = supabase
      .channel(`invitations-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "round_invitations",
          filter: `invited_user_id=eq.${user.id}`,
        },
        (payload) => {
          const invitation = payload.new as RoundInvitation;
          if (invitation.status === "pending") {
            handleInvitation(invitation);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, user?.id]);

  const handleInvitation = async (invitation: RoundInvitation) => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      // Mark invitation as accepted
      await supabase
        .from("round_invitations")
        .update({ status: "accepted" })
        .eq("id", invitation.id);

      // Show toast and navigate
      toast.success(`Joining round: ${invitation.round_name}`);
      
      // Navigate to the round
      navigate(`/round/${invitation.round_code}`);
    } catch (error) {
      console.error("Error handling invitation:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return { isProcessing };
};

// Function to create invitations for friends added to a round
export const createRoundInvitations = async (
  roundId: string,
  roundCode: string,
  roundName: string,
  inviterUserId: string,
  invitedUserIds: string[]
) => {
  if (invitedUserIds.length === 0) return;

  const invitations = invitedUserIds.map((userId) => ({
    round_id: roundId,
    round_code: roundCode,
    round_name: roundName,
    inviter_user_id: inviterUserId,
    invited_user_id: userId,
    status: "pending",
  }));

  const { error } = await supabase
    .from("round_invitations")
    .insert(invitations);

  if (error) {
    console.error("Error creating invitations:", error);
    throw error;
  }
};
